import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import ThemeSwitcher from "../components/ThemeSwitcher";
import { actionStyle, confColor, fmt } from "../lib/signals";
import WinRateBadge from "../components/WinRateBadge";
import { Pulse, ArrowRight, Lightning } from "@phosphor-icons/react";

function miniSent(s) {
  const sc = s?.score || 0;
  const label = sc > 25 ? "Bullish" : sc < -25 ? "Bearish" : "Neutral";
  const cls = sc > 25 ? "text-bull" : sc < -25 ? "text-bear" : "text-neutral";
  return <span className={`text-xs font-medium ${cls}`}>{label} {s?.mentions ? `· ${s.mentions}` : ""}</span>;
}

export default function Landing() {
  const [stats, setStats] = useState(null);
  const [rows, setRows] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/public/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/public/hotlist").then((r) => setRows(r.data.hotlist)).catch(() => {});
    const t = setInterval(() => api.get("/public/hotlist").then((r) => setRows(r.data.hotlist)).catch(() => {}), 20000);
    return () => clearInterval(t);
  }, []);

  const s = stats || {};
  return (
    <div className="min-h-screen">
      <header className="sticky top-0 z-40 px-4 pt-4">
        <div className="max-w-6xl mx-auto glass-strong rounded-full px-4 py-2.5 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full brand-gradient flex items-center justify-center shadow-lg"><Pulse size={17} weight="bold" className="text-white" /></div>
            <span className="font-display font-extrabold tracking-tight">Signal<span className="brand-text">Desk</span></span>
          </div>
          <div className="flex items-center gap-3">
            <ThemeSwitcher />
            <button data-testid="landing-login" onClick={() => navigate("/login")}
              className="brand-gradient text-white rounded-full px-4 py-2 text-sm font-semibold flex items-center gap-1.5 shadow-lg hover:opacity-90 transition-opacity duration-200">
              Desk Login <ArrowRight size={14} weight="bold" />
            </button>
          </div>
        </div>
      </header>

      <section className="max-w-6xl mx-auto px-6 pt-16 pb-10 text-center">
        <div className="inline-flex items-center gap-2 glass rounded-full px-4 py-1.5 text-xs font-medium mb-6 fade-up">
          <Lightning size={13} weight="fill" className="text-brand" /> Live from Telegram · WhatsApp · Reddit
        </div>
        <h1 className="font-display font-black tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[1.05] fade-up">
          Every trade call, <span className="brand-text">one intelligent desk.</span>
        </h1>
        <p className="text-base md:text-lg text-muted-foreground max-w-2xl mx-auto mt-5 fade-up" style={{ animationDelay: "0.1s" }}>
          We read the noise from your groups, extract the signals, confirm them with a real technical engine, and rank them by conviction & crowd sentiment — across US & global equities, indices, commodities and forex.
        </p>
        <div className="flex items-center justify-center gap-3 mt-7 fade-up" style={{ animationDelay: "0.2s" }}>
          <button onClick={() => navigate("/login")} className="brand-gradient text-white rounded-full px-6 py-3 font-semibold shadow-xl hover:opacity-90 transition-opacity duration-200 flex items-center gap-2">
            Configure your desk <ArrowRight size={16} weight="bold" />
          </button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-14 fade-up" style={{ animationDelay: "0.3s" }}>
          {[
            { k: "Instruments", v: s.instruments, c: "text-foreground" },
            { k: "Long ideas", v: s.long, c: "text-bull" },
            { k: "Short ideas", v: s.short, c: "text-bear" },
            { k: "Calls parsed", v: s.calls, c: "text-brand" },
          ].map((x) => (
            <div key={x.k} className="glass rounded-3xl p-5">
              <div className={`font-mono font-bold text-3xl ${x.c}`}>{x.v ?? "—"}</div>
              <div className="text-xs text-muted-foreground mt-1">{x.k}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 pb-20">
        <div className="glass-strong rounded-3xl overflow-hidden fade-up">
          <div className="flex items-center justify-between px-6 py-4 border-b border-border">
            <h2 className="font-display font-bold text-lg">Live Hot List</h2>
            <span className="text-xs text-muted-foreground">Public view · refreshes automatically</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[720px]">
              <thead>
                <tr className="text-[11px] uppercase tracking-wider text-muted-foreground border-b border-border">
                  <th className="text-left font-semibold px-6 py-3">Instrument</th>
                  <th className="text-left font-semibold px-3 py-3">Action</th>
                  <th className="text-right font-semibold px-3 py-3">Price</th>
                  <th className="text-right font-semibold px-3 py-3">Conviction</th>
                  <th className="text-left font-semibold px-3 py-3">Win Rate</th>
                  <th className="text-left font-semibold px-3 py-3">Sentiment</th>
                  <th className="text-left font-semibold px-6 py-3">Timeframe</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((h) => (
                  <tr key={h.id} data-testid={`landing-row-${h.ticker}`} className="border-b border-border/50 hover:bg-foreground/5 transition-colors duration-150">
                    <td className="px-6 py-3 font-semibold">{h.ticker} <span className="text-muted-foreground text-xs font-normal">{h.exchange}</span></td>
                    <td className="px-3 py-3"><span className={`inline-block px-2.5 py-1 rounded-full text-[11px] font-semibold border ${actionStyle(h.action)}`}>{h.action}</span></td>
                    <td className="px-3 py-3 text-right font-mono">{fmt(h.price)}</td>
                    <td className={`px-3 py-3 text-right font-mono font-bold ${confColor(h.confluence_score)}`}>{h.confluence_score}</td>
                    <td className="px-3 py-3"><WinRateBadge value={h.source_win_rate} /></td>
                    <td className="px-3 py-3">{miniSent(h.sentiment)}</td>
                    <td className="px-6 py-3 text-muted-foreground text-xs">{h.timeframe}</td>
                  </tr>
                ))}
                {rows.length === 0 && <tr><td colSpan={7} className="px-6 py-12 text-center text-muted-foreground">Warming up the desk…</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
        <p className="text-center text-xs text-muted-foreground mt-6">For internal reference only. Not investment advice.</p>
      </section>
    </div>
  );
}
